import React, { useEffect, useContext, useState, useRef, useMemo } from "react";
import { useHistory } from "react-router-dom";

// axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../BaseUrl";

// functions
import {
  getCookie,
  getSystemSettings,
  customStyle,
  formatPrice,
} from "../../../../functions/Functions";
import { useTranslation } from "react-i18next";

// 3rd party packages
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import Select from "react-select";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import { useReactToPrint } from "react-to-print";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// pages & includes
import ReportSidebar from "./ReportSidebar";

// context consumer
import { SettingsContext } from "../../../../contexts/Settings";
import { RestaurantContext } from "../../../../contexts/Restaurant";
import { UserContext } from "../../../../contexts/User";
import CustomSpinLoader from "../../../components/CustomSpinLoader";
import ReportTypeOptions from "../../../components/Reports/ReportTypeOptions";
import { useReportTypeOption } from "../../../../hooks/useReportTypeOption";
import { LOCATION_CONFIG } from "../manage/settings/LocationSetupConfig";

// ═══════════════════════════════════════════════════════════════
// CONSTANTS — Moved outside component to avoid re-creation
// ═══════════════════════════════════════════════════════════════

/**
 * Fallback column definitions when the API doesn't include
 * a `columns` array inside each period object.
 */
const WEEK_0_COLUMNS = [
  { key: "orders", label: "Orders", type: "number" },
  { key: "netSale", label: "Net Sale", type: "currency" },
  { key: "grossSale", label: "Gross Sale", type: "currency" },
  { key: "aov", label: "AOV", type: "currency" },
  { key: "guestCheck", label: "Guest Check", type: "currency" },
  { key: "diff", label: "Diff", type: "percentage" },

];

const AFTER_WEEK_0_COLUMNS = [
  { key: "orders", label: "Orders", type: "number" },
  { key: "netSale", label: "Net Sale", type: "currency" },
  { key: "grossSale", label: "Gross Sale", type: "currency" },
];

const DEFAULT_COLUMNS = WEEK_0_COLUMNS;

// ═══════════════════════════════════════════════════════════════
// HELPER FUNCTIONS — Pure utilities, no React dependency
// ═══════════════════════════════════════════════════════════════

/**
 * Sort party rows so "Total" always appears last.
 * Preserves API ordering for all other rows (Takeaway, Website, Dine-In, etc.)
 */
const getPartyRows = (weekRows = []) => {
  const normalRows = weekRows.filter(
    (row) => String(row.party).toLowerCase() !== "total"
  );
  const totalRow = weekRows.find(
    (row) => String(row.party).toLowerCase() === "total"
  );
  return totalRow ? [...normalRows, totalRow] : normalRows;
};

/**
 * Sort channel summary rows so "Total" always appears last.
 */
const getChannelRows = (weekRows = []) => {
  const normalRows = weekRows.filter(
    (row) => String(row.channel).toLowerCase() !== "total"
  );
  const totalRow = weekRows.find(
    (row) => String(row.channel).toLowerCase() === "total"
  );
  return totalRow ? [...normalRows, totalRow] : normalRows;
};

/**
 * Build channel name list + O(1) lookup from weeks map:
 * { week1: [{ channel, orders, ... }], ... }
 * → { channelNames, channelSummaryMap: { [weekKey]: { [channelName]: row } } }
 */
const buildChannelSummaryFromWeeks = (weeksByPeriod, periodsList = []) => {
  if (!weeksByPeriod || !periodsList.length) {
    return { channelNames: [], channelSummaryMap: {} };
  }

  const map = {};
  const seen = new Set();
  const ordered = [];

  periodsList.forEach((period) => {
    const weekRows = weeksByPeriod[period.key] || [];
    map[period.key] = {};
    getChannelRows(weekRows).forEach((row) => {
      map[period.key][row.channel] = row;
      if (!seen.has(row.channel)) {
        seen.add(row.channel);
        ordered.push(row.channel);
      }
    });
  });

  const channelNames = [
    ...ordered.filter((c) => String(c).toLowerCase() !== "total"),
    ...ordered.filter((c) => String(c).toLowerCase() === "total"),
  ];

  return { channelNames, channelSummaryMap: map };
};

/** Metric columns for the channel-wise summary mini-tables under Grand Total */
const CHANNEL_SUMMARY_COLUMNS = [
  { key: "orders", label: "Orders", type: "number" },
  { key: "netSale", label: "Net Sale", type: "currency" },
  { key: "grossSale", label: "Gross Sale", type: "currency" },
  { key: "aov", label: "AOV", type: "currency" },
];

/**
 * Format a cell value based on its column type.
 * @param {*} value - Raw value from API
 * @param {string} type - "currency" | "percentage" | "number"
 */
const formatValue = (value, type) => {
  if (value === null || value === undefined || value === "") return "-";
  if (type === "currency") return formatPrice(Number(value) || 0);
  if (type === "percentage") return value;
  return value;
};

/** Show toast error with consistent styling */
const showToastError = (message) => {
  toast.error(message, {
    position: "bottom-center",
    closeButton: false,
    autoClose: 10000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    className: "text-center toast-notification",
  });
};

// ═══════════════════════════════════════════════════════════════
// SUB-COMPONENT: WeeklySalesTable
//
// API Response Structure:
// {
//   month: "September 2026",
//   periods: [{ key: "week1", label: "Week 1", columns: [...] }, ...],
//   branches: [{ key, name, weeks: { week1: [{party, orders, ...}] } }],
//   channel_wise_summary: { weeks: { week1: [{ channel, orders, netSale, grossSale, aov }], ... } }
//   location_wise_channel_summary: [{ city_id, city_name, columns, weeks, rows }]
// }
// ═══════════════════════════════════════════════════════════════

const WeeklySalesTable = ({ reportData, t }) => {
  const periods = reportData?.periods || [];   // Source of truth for which weeks exist

  // Normalize locations structure (handles both new { locations: [...] } and legacy { branches: [...] })
  const locations = useMemo(() => {
    if (Array.isArray(reportData?.locations) && reportData.locations.length > 0) {
      return reportData.locations.filter(
        (loc) => Array.isArray(loc.branches) && loc.branches.length > 0
      );
    }
    if (Array.isArray(reportData?.branches) && reportData.branches.length > 0) {
      return [{ city_name: null, branches: reportData.branches }];
    }
    return [];
  }, [reportData]);

  // Flattened list of all branches across all locations (used for grandTotals, checks, etc.)
  const allBranches = useMemo(() => {
    return locations.flatMap((loc) => loc.branches || []);
  }, [locations]);

  const channelSummaryWeeks = reportData?.channel_wise_summary?.weeks || null;

  /**
   * Channel list + O(1) lookup for channel_wise_summary.weeks
   * channelSummaryMap: { [weekKey]: { [channelName]: row } }
   */
  const { channelNames, channelSummaryMap } = useMemo(
    () => buildChannelSummaryFromWeeks(channelSummaryWeeks, periods),
    [channelSummaryWeeks, periods]
  );

  /**
   * Per-location channel summaries shown before overall Channel Summary.
   * Uses location.weeks (same shape as channel_wise_summary.weeks).
   */
  const locationWiseChannelSummaries = useMemo(() => {
    const list = Array.isArray(reportData?.location_wise_channel_summary)
      ? reportData.location_wise_channel_summary
      : [];

    return list
      .map((loc, index) => {
        const { channelNames: names, channelSummaryMap: map } =
          buildChannelSummaryFromWeeks(loc?.weeks, periods);
        const columns =
          Array.isArray(loc?.columns) && loc.columns.length > 0
            ? loc.columns
            : CHANNEL_SUMMARY_COLUMNS;

        return {
          key: loc?.city_id ?? loc?.city_name ?? index,
          cityName: loc?.city_name || "Unknown Location",
          columns,
          channelNames: names,
          channelSummaryMap: map,
        };
      })
      .filter((loc) => loc.channelNames.length > 0);
  }, [reportData, periods]);

  /**
   * columnsMap: O(1) lookup for each period's column definitions.
   * Key = period.key (e.g. "week1"), Value = columns array.
   * Falls back to DEFAULT_COLUMNS if period has no columns.
   */
  const columnsMap = useMemo(() => {
    const map = {};
    periods.forEach((period, index) => {
      // const targetColumns = index === 0 ? WEEK_0_COLUMNS : AFTER_WEEK_0_COLUMNS;
      const targetColumns = WEEK_0_COLUMNS
      // Pick only allowed columns matching targetColumns (ignoring extra API columns)
      const cols = targetColumns.map((defCol) => {
        const apiCol = period.columns?.find((c) => c.key === defCol.key);
        return apiCol || defCol;
      });

      map[period.key] = cols;
    });
    return map;
  }, [periods]);

  /** Get columns for a week from the pre-built map */
  const getColumns = (weekKey) => columnsMap[weekKey] || DEFAULT_COLUMNS;

  /**
   * Total <td>/<th> cells per row:
   * 1 (label column) + sum of all period column counts.
   */
  const totalColSpan = useMemo(() => {
    return 1 + periods.reduce((total, p) => total + getColumns(p.key).length, 0);
  }, [periods, columnsMap]);

  /**
   * Calculate grand total across all branches for each period.
   * Result: { [weekKey]: { orders, netSale, grossSale, ... } }
   */
  const grandTotals = useMemo(() => {
    const totals = {};
    periods.forEach((period, periodIdx) => {
      const weekKey = period.key;
      const columns = getColumns(weekKey);
      const row = {};

      // Gather total rows for this week across all branches
      const branchTotals = allBranches
        .map((branch) => {
          const weekRows = branch.weeks?.[weekKey] || [];
          const found = weekRows.find(
            (r) => String(r.party).toLowerCase() === "total"
          );
          if (found) return found;

          // Fallback: sum party rows if no explicit "Total" row exists
          const normalRows = weekRows.filter(
            (r) => String(r.party).toLowerCase() !== "total"
          );
          if (!normalRows.length) return null;
          const fallback = {};
          columns.forEach((col) => {
            if (col.type !== "percentage") {
              fallback[col.key] = normalRows.reduce(
                (sum, r) => sum + (Number(r[col.key]) || 0),
                0
              );
            }
          });
          return fallback;
        })
        .filter(Boolean);

      // If only 1 branch, preserve its exact pre-calculated values
      if (allBranches.length === 1 && branchTotals[0]) {
        columns.forEach((col) => {
          row[col.key] = branchTotals[0][col.key] ?? 0;
        });
      } else {
        // Multiple branches: sum numeric / currency fields
        columns.forEach((col) => {
          if (col.key === "diff" || col.key === "aov" || col.key === "guestCheck") {
            return;
          }
          row[col.key] = branchTotals.reduce(
            (sum, bt) => sum + (Number(bt[col.key]) || 0),
            0
          );
        });

        const orders = Number(row.orders) || 0;
        const netSale = Number(row.netSale) || 0;

        // AOV = Net Sale / Orders
        row.aov = orders > 0 ? Math.round((netSale / orders) * 100) / 100 : 0;
        // Guest Check = Net Sale / Guests (or Orders if guests not tracked)
        const totalGuests = branchTotals.reduce(
          (sum, bt) => sum + (Number(bt.guests) || 0),
          0
        );
        const guestDivisor = totalGuests > 0 ? totalGuests : orders;
        row.guestCheck = guestDivisor > 0 ? Math.round((netSale / guestDivisor) * 100) / 100 : 0;

        // % Diff: difference of netSale compared to previous period
        if (periodIdx === 0) {
          row.diff = orders > 0 ? "0%" : "-100%";
        } else {
          const prevKey = periods[periodIdx - 1]?.key;
          const prevNet = Number(totals[prevKey]?.netSale) || 0;
          if (prevNet > 0) {
            const diffPct = Math.round(((netSale - prevNet) / prevNet) * 100);
            row.diff = `${diffPct > 0 ? "+" : ""}${diffPct}%`;
          } else {
            row.diff = netSale > 0 ? "+100%" : "0%";
          }
        }
      }

      totals[weekKey] = row;
    });
    return totals;
  }, [periods, allBranches, columnsMap]);

  // ─── No data state ───
  if (!allBranches.length) {
    return (
      <div className="text-center py-5 text-muted bg-light rounded border">
        <h6 className="mb-0">{t("No records found")}</h6>
      </div>
    );
  }

  return (
    <div className="table-responsive" style={{ overflowX: "auto", background: "#fff" }}>
      <table
        id="weekly-sales-report-table"
        className="table table-bordered mb-0"
        style={{ fontSize: "12px", whiteSpace: "nowrap" }}
      >
        {/* ═════════════ TABLE HEADER ═════════════ */}
        <thead>
          {/* Row 1: Month title spanning all columns */}
          {reportData?.month && (
            <tr>
              <th
                colSpan={totalColSpan}
                style={{ textAlign: "center", fontWeight: "700", fontSize: "14px", background: "#f0f4f8" }}
              >
                {reportData.month}
              </th>
            </tr>
          )}

          {/* Row 2: Week group header */}
          <tr>
            <th
              rowSpan={3}
              style={{ minWidth: "180px", verticalAlign: "middle", textAlign: "center", fontWeight: "700" }}
            >
              {t("Branch / Channel")}
            </th>
          </tr>

          {/* Row 3: Day range sub-header */}
          <tr>
            {periods.map((period) => {
              const columns = getColumns(period.key);
              return (
                <th
                  key={`range-${period.key}`}
                  colSpan={columns.length}
                  style={{ textAlign: "center", fontWeight: "600", verticalAlign: "middle" }}
                >
                  {t(period?.label)}
                </th>
              );
            })}
          </tr>

          {/* Row 4: Metric sub-headers (Orders, Net Sale, ...) */}
          <tr>
            {periods.map((period) =>
              getColumns(period.key).map((column) => (
                <th
                  key={`${period.key}-${column.key}`}
                  style={{ textAlign: "center", fontWeight: "600", minWidth: "100px" }}
                >
                  {t(column.label)}
                </th>
              ))
            )}
          </tr>
        </thead>

        {/* ═════════════ TABLE BODY ═════════════ */}
        <tbody>
          {locations.map((loc, locIndex) => (
            <React.Fragment key={loc.city_id || loc.city_name || locIndex}>
              {/* City name header row */}
              {loc.city_name && (
                <tr>
                  <td
                    colSpan={totalColSpan}
                    style={{
                      fontWeight: "800",
                      fontSize: "14px",
                      padding: "8px 12px",
                      background: "#e2e8f0",
                      color: "#1e293b",
                      textTransform: "uppercase",
                    }}
                  >
                    <strong>{loc.city_name}</strong>
                  </td>
                </tr>
              )}

              {/* Branch loop (same structure as before) */}
              {loc.branches?.map((branchItem, branchIndex) => {
                /*
                 * Single pass over periods to:
                 * 1. Collect unique party names (Set for O(1) uniqueness)
                 * 2. Build partyMap for O(1) data lookup: { weekKey: { partyName: rowData } }
                 * "Total" party is always pushed last via getPartyRows.
                 */
                const partySet = new Set();
                const allPartyNames = [];
                const partyMap = {};

                periods.forEach((period) => {
                  const weekRows = branchItem.weeks?.[period.key] || [];
                  // Build partyMap for this week
                  partyMap[period.key] = {};
                  weekRows.forEach((row) => {
                    partyMap[period.key][row.party] = row;
                  });
                  // Collect unique party names (preserving order, Total last)
                  getPartyRows(weekRows).forEach((row) => {
                    if (!partySet.has(row.party)) {
                      partySet.add(row.party);
                      allPartyNames.push(row.party);
                    }
                  });
                });

                return (
                  <React.Fragment key={branchItem.key || branchIndex}>
                    {/* Branch name header row */}
                    <tr>
                      <td
                        colSpan={totalColSpan}
                        style={{ fontWeight: "700", fontSize: "13px", padding: "8px", background: "#f8f9fa" }}
                      >
                        <strong>{branchItem.name}</strong>
                      </td>
                    </tr>

                    {/* Party data rows (Takeaway, Website, Dine-In, Total) */}
                    {allPartyNames.map((partyName, partyIdx) => {
                      const isTotal = String(partyName).toLowerCase() === "total";
                      const CellTag = isTotal ? "th" : "td";
                      return (
                        <React.Fragment key={`${branchItem.key}-${partyName}-${partyIdx}`}>
                          <tr style={isTotal ? { fontWeight: "700", background: "#f0f4f8" } : {}}>
                            <CellTag style={{
                              fontWeight: isTotal ? "700" : "400",
                              paddingLeft: "15px",
                              textAlign: "left",
                              ...(isTotal ? { background: "#f0f4f8" } : {}),
                            }}>
                              {partyName}
                            </CellTag>

                            {/* Data cells */}
                            {periods.map((period) => {
                              const partyRow = partyMap[period.key]?.[partyName];
                              return getColumns(period.key).map((column) => (
                                <CellTag
                                  key={`${period.key}-${partyName}-${column.key}`}
                                  className="text-end"
                                  style={{
                                    minWidth: "100px",
                                    ...(isTotal ? { fontWeight: "700", background: "#f0f4f8" } : {}),
                                  }}
                                >
                                  {formatValue(partyRow ? (partyRow[column.key] ?? 0) : 0, column.type)}
                                </CellTag>
                              ));
                            })}
                          </tr>
                          {/* 2 empty rows after Total for spacing */}
                          {isTotal && (
                            <>
                              <tr><td colSpan={totalColSpan} style={{ padding: "2px", border: "none" }}>&nbsp;</td></tr>
                              <tr><td colSpan={totalColSpan} style={{ padding: "2px", border: "none" }}>&nbsp;</td></tr>
                            </>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          ))}

          {/* ─── Grand Total Section (Rendered in <th> like header) ─── */}
          <tr style={{ fontWeight: "700", background: "#e2e8f0" }}>
            <th
              style={{
                fontWeight: "700",
                background: "#e2e8f0",
                paddingLeft: "15px",
                textAlign: "left",
              }}
            >
              {t("Grand Total")}
            </th>

            {periods.map((period) => {
              const weekTotal = grandTotals[period.key];
              return getColumns(period.key).map((column) => (
                <th
                  key={`grand-total-${period.key}-${column.key}`}
                  className="text-end"
                  style={{
                    minWidth: "100px",
                    fontWeight: "700",
                    background: "#e2e8f0",
                  }}
                >
                  {formatValue(weekTotal ? (weekTotal[column.key] ?? 0) : 0, column.type)}
                </th>
              ));
            })}
          </tr>

          {/* 2 empty rows after Grand Total for spacing before channel summaries */}
          <tr><td colSpan={totalColSpan} style={{ padding: "2px", border: "none" }}>&nbsp;</td></tr>
          <tr><td colSpan={totalColSpan} style={{ padding: "2px", border: "none" }}>&nbsp;</td></tr>

          {/* ─── Location-wise Channel Summary (before overall Channel Summary) ─── */}
          {locationWiseChannelSummaries.map((locSummary) => {
            const summaryCols = locSummary.columns || CHANNEL_SUMMARY_COLUMNS;

            return (
              <React.Fragment key={`loc-ch-sum-${locSummary.key}`}>
                <tr>
                  <td
                    colSpan={totalColSpan}
                    style={{
                      fontWeight: "800",
                      fontSize: "14px",
                      padding: "8px 12px",
                      background: "#e2e8f0",
                      color: "#1e293b",
                      textTransform: "uppercase",
                    }}
                  >
                    <strong>
                      {locSummary.cityName} — {t("Channel Summary")}
                    </strong>
                  </td>
                </tr>

                <tr>
                  <th
                    style={{
                      fontWeight: "700",
                      padding: "8px",
                      background: "#e2e8f0",
                      textAlign: "left",
                    }}
                  >
                    {t("Channel")}
                  </th>
                  {periods.map((period) => {
                    const weekCols = getColumns(period.key);
                    const padCount = Math.max(0, weekCols.length - summaryCols.length);
                    return (
                      <React.Fragment key={`loc-ch-sum-h-${locSummary.key}-${period.key}`}>
                        {summaryCols.map((col) => (
                          <th
                            key={`loc-ch-sum-h-${locSummary.key}-${period.key}-${col.key}`}
                            style={{
                              fontWeight: "600",
                              textAlign: "center",
                              background: "#e2e8f0",
                              minWidth: "100px",
                            }}
                          >
                            {t(col.label)}
                          </th>
                        ))}
                        {padCount > 0 && (
                          <th colSpan={padCount} style={{ background: "#e2e8f0" }}>
                            &nbsp;
                          </th>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tr>

                {locSummary.channelNames.map((channelName) => {
                  const isTotal = String(channelName).toLowerCase() === "total";
                  const CellTag = isTotal ? "th" : "td";
                  const cellStyle = isTotal
                    ? { fontWeight: "700", background: "#f0f4f8" }
                    : {};

                  return (
                    <tr
                      key={`loc-ch-sum-${locSummary.key}-${channelName}`}
                      style={isTotal ? { fontWeight: "700", background: "#f0f4f8" } : {}}
                    >
                      <CellTag
                        style={{
                          fontWeight: isTotal ? "700" : "600",
                          paddingLeft: "15px",
                          textAlign: "left",
                          ...cellStyle,
                        }}
                      >
                        {channelName}
                      </CellTag>

                      {periods.map((period) => {
                        const weekCols = getColumns(period.key);
                        const padCount = Math.max(0, weekCols.length - summaryCols.length);
                        const row =
                          locSummary.channelSummaryMap[period.key]?.[channelName];

                        return (
                          <React.Fragment
                            key={`loc-ch-sum-${locSummary.key}-${period.key}-${channelName}`}
                          >
                            {summaryCols.map((col) => (
                              <CellTag
                                key={`loc-ch-sum-${locSummary.key}-${period.key}-${channelName}-${col.key}`}
                                className="text-end"
                                style={{ minWidth: "100px", ...cellStyle }}
                              >
                                {formatValue(row ? (row[col.key] ?? 0) : 0, col.type)}
                              </CellTag>
                            ))}
                            {padCount > 0 && (
                              <CellTag colSpan={padCount} style={cellStyle}>
                                &nbsp;
                              </CellTag>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  );
                })}

                <tr>
                  <td colSpan={totalColSpan} style={{ padding: "2px", border: "none" }}>
                    &nbsp;
                  </td>
                </tr>
              </React.Fragment>
            );
          })}

          {/* ─── Channel Wise Summary (side-by-side per week, under Grand Total) ─── */}
          {channelNames.length > 0 && (
            <>
              {/* Metric headers aligned under each week group */}
              <tr>
                <th
                  style={{
                    fontWeight: "700",
                    padding: "8px",
                    background: "#e2e8f0",
                    textAlign: "left",
                  }}
                >
                  {t("Channel Summary")}
                </th>
                {periods.map((period) => {
                  const weekCols = getColumns(period.key);
                  const padCount = Math.max(0, weekCols.length - CHANNEL_SUMMARY_COLUMNS.length);
                  return (
                    <React.Fragment key={`ch-sum-h-${period.key}`}>
                      {CHANNEL_SUMMARY_COLUMNS.map((col) => (
                        <th
                          key={`ch-sum-h-${period.key}-${col.key}`}
                          style={{
                            fontWeight: "600",
                            textAlign: "center",
                            background: "#e2e8f0",
                            minWidth: "100px",
                          }}
                        >
                          {t(col.label)}
                        </th>
                      ))}
                      {padCount > 0 && (
                        <th
                          colSpan={padCount}
                          style={{ background: "#e2e8f0" }}
                        >
                          &nbsp;
                        </th>
                      )}
                    </React.Fragment>
                  );
                })}
              </tr>

              {/* One row per channel; values under each week */}
              {channelNames.map((channelName) => {
                const isTotal = String(channelName).toLowerCase() === "total";
                const CellTag = isTotal ? "th" : "td";
                const cellStyle = isTotal
                  ? { fontWeight: "700", background: "#f0f4f8" }
                  : {};

                return (
                  <tr
                    key={`ch-sum-${channelName}`}
                    style={isTotal ? { fontWeight: "700", background: "#f0f4f8" } : {}}
                  >
                    <CellTag
                      style={{
                        fontWeight: isTotal ? "700" : "600",
                        paddingLeft: "15px",
                        textAlign: "left",
                        ...cellStyle,
                      }}
                    >
                      {channelName}
                    </CellTag>

                    {periods.map((period) => {
                      const weekCols = getColumns(period.key);
                      const padCount = Math.max(
                        0,
                        weekCols.length - CHANNEL_SUMMARY_COLUMNS.length
                      );
                      const row = channelSummaryMap[period.key]?.[channelName];

                      return (
                        <React.Fragment key={`ch-sum-${period.key}-${channelName}`}>
                          {CHANNEL_SUMMARY_COLUMNS.map((col) => (
                            <CellTag
                              key={`ch-sum-${period.key}-${channelName}-${col.key}`}
                              className="text-end"
                              style={{ minWidth: "100px", ...cellStyle }}
                            >
                              {formatValue(row ? (row[col.key] ?? 0) : 0, col.type)}
                            </CellTag>
                          ))}
                          {padCount > 0 && (
                            <CellTag colSpan={padCount} style={cellStyle}>
                              &nbsp;
                            </CellTag>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tr>
                );
              })}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT: WeeklySalesReport
// Page-level component with filters (month picker, branch select),
// API integration, and Excel export functionality.
// ═══════════════════════════════════════════════════════════════

const WeeklySalesReport = () => {
  const { t } = useTranslation();
  const history = useHistory();

  // ─── Context ───
  const { loading, setLoading, dataPaginating, setDataPaginating, generalSettings, fetchCoverageList } = useContext(SettingsContext);
  const { branchForSearch } = useContext(RestaurantContext);
  const { authUserInfo } = useContext(UserContext);

  // ─── Component State ───
  const [reportData, setReportData] = useState(null);        // Parsed API report data
  const componentRef = useRef();                              // Ref for ReactHTMLTableToExcel
  const printRef = useRef();                                  // Ref for print

  const [selectedDate, setSelectedDate] = useState(null); // Month/Year picker (optional)
  const [startDate, setStartDate] = useState(null);         // Start date (optional with end date)
  const [endDate, setEndDate] = useState(null);             // End date (optional with start date)
  const [branch, setBranch] = useState(null);               // Selected branch(es)
  const [province, setProvince] = useState(null);
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [city, setCity] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [cityLoading, setCityLoading] = useState(false);
  const [generatedReport, setGeneratedReport] = useState(false); // Whether report was fetched
  const [siteName, setSiteName] = useState("");                  // Site name for page title
  const [branchSearch, setBranchSearch] = useState(null);        // Branch options for Select

  const { selectedOption, handleOptionChange } = useReportTypeOption("view");

  const hasReportData = useMemo(() => {
    if (!reportData) return false;
    if (Array.isArray(reportData.locations) && reportData.locations.length > 0) {
      return reportData.locations.some(
        (loc) => Array.isArray(loc.branches) && loc.branches.length > 0
      );
    }
    if (Array.isArray(reportData.branches) && reportData.branches.length > 0) {
      return true;
    }
    return false;
  }, [reportData]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: `@page {margin: 5px; }`,
  });

  // ─── Initialization ───
  useEffect(() => {
    setSiteName(getSystemSettings(generalSettings, "siteName"));

    // Staff/Manager users can only see their own branch
    const userType = authUserInfo?.details?.user_type;
    if (userType === "staff" || userType === "manager") {
      setBranchSearch(
        branchForSearch.filter((b) => b.id === authUserInfo?.details?.branch_id)
      );
    } else {
      setBranchSearch(branchForSearch);
    }

    setLoading(true);
    setTimeout(() => setLoading(false), 500);
  }, [generalSettings, branchForSearch, authUserInfo]);

  useEffect(() => {
    if (typeof fetchCoverageList !== "function") return;
    fetchCoverageList("province", 1)
      .then((parsed) => setProvinceOptions(parsed?.activeList || []))
      .catch(() => setProvinceOptions([]));
  }, [fetchCoverageList]);

  // ─── Event Handlers ───
  const handleBranch = (selectedBranch) => setBranch(selectedBranch);
  const handleProvince = (selectedProvince) => {
    setProvince(selectedProvince);
    setCity([]);
    setCityOptions([]);
    setCityLoading(Boolean(selectedProvince));
    if (!selectedProvince) return;

    axios.get(`${BASE_URL}${LOCATION_CONFIG.city.endpoint}/get-by-province/${selectedProvince.id}`, {
      headers: { Authorization: `Bearer ${getCookie()}` },
    }).then((res) => {
      const cities = res.data?.data;
      setCityOptions(Array.isArray(cities) ? cities : []);
    }).catch(() => setCityOptions([])).finally(() => setCityLoading(false));
  };

  const handleMonthChange = (date) => {
    setSelectedDate(date);
    if (date) {
      setStartDate(null);
      setEndDate(null);
    }
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
    if (date) setSelectedDate(null);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    if (date) setSelectedDate(null);
  };

  const exportFileSuffix = useMemo(() => {
    if (selectedDate) return moment(selectedDate).format("YYYY-MM");
    if (startDate && endDate) {
      return `${moment(startDate).format("YYYY-MM-DD")}_to_${moment(endDate).format("YYYY-MM-DD")}`;
    }
    return "report";
  }, [selectedDate, startDate, endDate]);

  // ─── API Call ───
  /**
   * Fetches weekly sales report from:
   * GET /settings/weekly-sales-report?month=YYYY-MM&branch_id=...
   * or
   * GET /settings/weekly-sales-report?start_date=YYYY-MM-DD&end_date=YYYY-MM-DD&branch_id=...
   *
   * Use either month OR start_date + end_date (optional modes).
   * Response shape: { status: true, message: "...", data: { month, periods, branches, channel_wise_summary: { weeks } } }
   */
  const getWeeklySalesReport = () => {
    // Reset previous report
    setReportData(null);
    setGeneratedReport(false);

    // Validate: Staff/Manager must select a branch
    const userType = authUserInfo?.details?.user_type;
    if (
      (userType === "staff" || userType === "manager") &&
      (!branch || (Array.isArray(branch) && branch.length === 0))
    ) {
      showToastError(t("Please select Branch to generate report"));
      return;
    }

    const hasMonth = Boolean(selectedDate);
    const hasStart = Boolean(startDate);
    const hasEnd = Boolean(endDate);

    if (!hasMonth && !hasStart && !hasEnd) {
      showToastError(t("Please select month or start/end date"));
      return;
    }

    if (hasMonth && (hasStart || hasEnd)) {
      showToastError(t("Use either month or start/end date, not both"));
      return;
    }

    if (!hasMonth && (hasStart !== hasEnd)) {
      showToastError(t("Please select both start date and end date"));
      return;
    }

    if (!hasMonth && hasStart && hasEnd && moment(startDate).isAfter(moment(endDate), "day")) {
      showToastError(t("Start date cannot be after end date"));
      return;
    }

    setDataPaginating(true);

    // Build query params — month OR date range
    const params = {};
    if (hasMonth) {
      params.month = moment(selectedDate).format("YYYY-MM");
    } else {
      params.start_date = moment(startDate).format("YYYY-MM-DD");
      params.end_date = moment(endDate).format("YYYY-MM-DD");
    }

    if (branch) {
      if (Array.isArray(branch) && branch.length > 0) {
        params.branch_id = branch.map((b) => b.id).join(",");
      } else if (branch.id) {
        params.branch_id = branch.id;
      }
    }
    if (province) params.state_ids = province.id;
    if (city.length) params.city_ids = city.map((item) => item.id).join(",");

    const url = `${BASE_URL}/settings/weekly-sales-report`;

    return axios
      .get(url, {
        params,
        headers: { Authorization: `Bearer ${getCookie()}` },
      })
      .then((res) => {
        setGeneratedReport(true);
        // API wraps report in { status, message, data: {...} }
        const report = res.data?.data || res.data;
        setReportData(report);
        setDataPaginating(false);

        // Trigger output after React renders the table
        if (selectedOption === "excel") {
          setTimeout(() => {
            if (componentRef.current) {
              componentRef.current.handleDownload();
            }
          }, 300);
        } else if (selectedOption === "print") {
          setTimeout(() => {
            handlePrint();
          }, 300);
        }
        // "view" — table is shown below; no extra action
      })
      .catch((error) => {
        setDataPaginating(false);
        const errMsg = error.response?.data?.message || error.message || "Failed to fetch report";
        showToastError(t(errMsg));
      });
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  return (
    <>
      <Helmet>
        <title>{t("Weekly Sales Report")} - {siteName}</title>
      </Helmet>

      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">

            {/* ─── Left Sidebar ─── */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ReportSidebar />
            </div>

            {/* ─── Main Content Area ─── */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div className="fk-scroll--pos-menu table-bottom-info-hide" data-simplebar>
                  <div className="t-pl-15 t-pr-15">

                    {/* Loading spinner */}
                    <CustomSpinLoader loading={loading || dataPaginating} />

                    <div key="weekly-sales-form">

                      {/* ─── Page Header & Filter Controls ─── */}
                      <div className="row gx-2 align-items-center t-pt-15 t-pb-15">
                        {/* Title */}
                        <div className="col-md-4 t-mb-15 mb-md-0">
                          <ul className="t-list fk-breadcrumb mb-0">
                            <li className="fk-breadcrumb__list">
                              <span className="t-link fk-breadcrumb__link text-capitalize font-weight-bold">
                                {t("Weekly Sales Report")}
                              </span>
                            </li>
                          </ul>
                        </div>

                        {/* View / Excel / Print */}
                        <div className="col-md-8 d-flex justify-content-md-end t-mb-15 mb-md-0">
                          <ReportTypeOptions
                            selectedOption={selectedOption}
                            onChange={handleOptionChange}
                            view={true}
                          />
                        </div>
                      </div>

                      <div className="row gx-2 align-items-center t-pb-15">
                        {/* Filters: Start/End Date OR Month + Branch + Generate */}
                        <div className="col-12 d-flex align-items-center justify-content-start gap-2 flex-wrap">
                          {/* Start Date */}
                          <div style={{ width: "140px", minWidth: "140px" }}>
                            <DatePicker
                              selected={startDate}
                              onChange={handleStartDateChange}
                              peekNextMonth
                              showMonthDropdown
                              showYearDropdown
                              dropdownMode="select"
                              dateFormat="dd/MM/yyyy"
                              className="form-control sm-text py-2"
                              placeholderText={t("Start Date")}
                              maxDate={endDate || undefined}
                              isClearable
                            />
                          </div>

                          {/* End Date */}
                          <div style={{ width: "140px", minWidth: "140px" }}>
                            <DatePicker
                              selected={endDate}
                              onChange={handleEndDateChange}
                              peekNextMonth
                              showMonthDropdown
                              showYearDropdown
                              dropdownMode="select"
                              dateFormat="dd/MM/yyyy"
                              className="form-control sm-text py-2"
                              placeholderText={t("End Date")}
                              minDate={startDate || undefined}
                              isClearable
                            />
                          </div>

                          {/* Month/Year Picker (alternative to date range) */}
                          <div style={{ minWidth: "120px" }}>
                            <DatePicker
                              selected={selectedDate}
                              onChange={handleMonthChange}
                              showMonthYearPicker
                              dateFormat="MM/yyyy"
                              className="form-control sm-text py-2"
                              placeholderText={t("Select Month")}
                              isClearable
                            />
                          </div>

                          {/* Province Filter */}
                          <div style={{ minWidth: "160px" }}>
                            <Select
                              options={provinceOptions}
                              value={province}
                              onChange={handleProvince}
                              getOptionLabel={(option) => option.name}
                              getOptionValue={(option) => option.id}
                              isSearchable
                              isClearable
                              placeholder={t("Select Province")}
                              className="sm-text"
                              styles={customStyle}
                            />
                          </div>

                          {/* City Filter */}
                          <div style={{ minWidth: "250px" }}>
                            <Select
                              options={cityOptions}
                              value={city}
                              onChange={setCity}
                              getOptionLabel={(option) => option.name}
                              getOptionValue={(option) => option.id}
                              isMulti
                              isSearchable
                              isClearable
                              isDisabled={!province}
                              isLoading={cityLoading}
                              loadingMessage={() => t("Loading cities...")}
                              noOptionsMessage={() => cityLoading ? null : t("No options")}
                              classNamePrefix="select"
                              maxMenuHeight="200px"
                              placeholder={t("Select City")}
                              className="sm-text"
                              styles={customStyle}
                            />
                          </div>

                          {/* Branch Multi-Select */}
                          <div style={{ minWidth: "250px" }}>
                            <Select
                              options={branchSearch}
                              value={branch}
                              onChange={handleBranch}
                              isSearchable={true}
                              isClearable={true}
                              getOptionLabel={(option) => option.name}
                              getOptionValue={(option) => option.id}
                              isMulti
                              placeholder={t("Select Branch")}
                              className="sm-text"
                              styles={customStyle}
                            />
                          </div>

                          {/* Generate Report Button */}
                          <div className="ml-auto">
                            <button
                              type="button"
                              className="btn btn-primary px-4 py-2 sm-text"
                              onClick={getWeeklySalesReport}
                            >
                              {t("Generate")}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* ─── Hidden Excel Export Button ─── */}
                      {reportData && (
                        <ReactHTMLTableToExcel
                          id="test-table-xls-button"
                          className="d-none"
                          table="weekly-sales-report-table"
                          filename={`Weekly-Sales-Report-${exportFileSuffix}`}
                          sheet="weekly-sales-report"
                          buttonText="Download XLS"
                          ref={componentRef}
                        />
                      )}

                      {/* ─── Report Table Output ─── */}
                      <div className="row t-mt-20 mb-4">
                        <div className="col-12" ref={printRef}>
                          {generatedReport && hasReportData ? (
                            <WeeklySalesTable reportData={reportData} t={t} />
                          ) : generatedReport ? (
                            <div className="text-center py-5 text-muted bg-light rounded border">
                              <h6 className="mb-0">
                                {t("No records found for selected filters")}
                              </h6>
                            </div>
                          ) : null}
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* ─── End Main Content ─── */}

          </div>
        </div>
      </main>
    </>
  );
};

export default WeeklySalesReport;
